// Free-tier guard mixin (Options API).
//
// Provides a snappy client-side pre-check before opening a "New ..." form for
// the limited resources (users / workcenters / workers). Real enforcement is
// server-side (it returns HTTP 402 with `error === 'tier_limit_exceeded'`,
// handled globally in plugins/http/interceptors.js) — this mixin only avoids a
// pointless round-trip and surfaces the upgrade prompt earlier.
//
// All tier numbers come from the store getters (sourced from /auth/me) — never
// hard-code limits here. All visible strings go through this.translate().
//
// Import LOCALLY into the consumer (the global mixin lives in main.js); this is
// not registered globally.

import { mapGetters } from 'vuex'

// One-line telemetry so free->paid intent is measurable even without a payment
// flow. Kept intentionally minimal (console.info) — swap for a real telemetry
// sink if/when one exists.
function logTierEvent(event, payload) {
  try {
    // eslint-disable-next-line no-console
    console.info('[tier]', event, payload || {})
  } catch (e) {
    /* never let telemetry break UX */
  }
}

export default {
  computed: {
    ...mapGetters(['isFreeTier', 'tierLimits', 'tierUsage', 'isSignupAdmin', 'showBillingUi']),
  },

  methods: {
    // kind: 'users' | 'workcenters' | 'workers' | 'activeOrders'
    canAdd(kind) {
      if (!this.isFreeTier) return true
      const used = this.tierUsage?.[kind] ?? 0
      const max = this.tierLimits?.[kind]
      // Missing limit => don't block (defensive; server still enforces).
      if (max === undefined || max === null) return true
      return used < max
    },

    // Pre-check before running `proceed` (e.g. navigate to the New form).
    guardAdd(kind, proceed) {
      if (this.canAdd(kind)) {
        return proceed()
      }
      this.openUpgradeDialog(kind)
    },

    // Show the upgrade prompt. `info` may carry { limit, current, max } from a
    // server 402 body; otherwise we derive current/max from the store getters
    // for the given kind.
    openUpgradeDialog(kind, info) {
      const limit = (info && info.limit) || kind
      const current = info && info.current != null
        ? info.current
        : (this.tierUsage?.[kind] ?? '')
      const max = info && info.max != null
        ? info.max
        : (this.tierLimits?.[kind] ?? '')

      logTierEvent('upgrade_prompt_shown', { limit, current, max })

      // translateFormat uses positional {0}/{1}/{2} placeholders fed a single
      // comma-joined args string.
      const html = this.translateFormat(
        "You've reached your {0} limit ({1}/{2}) on the Always Free plan. Upgrade to add more.",
        [this.translate(this.limitLabel(limit)), current, max].join(',')
      )

      return this.$swal({
        icon: 'info',
        title: this.translate('Upgrade your plan'),
        html: html,
        showCancelButton: true,
        confirmButtonText: this.translate('Contact us to upgrade'),
        cancelButtonText: this.translate('Not now'),
      }).then((result) => {
        if (result && result.isConfirmed) {
          logTierEvent('upgrade_clicked', { limit, current, max })
          // Open the in-app upgrade-request form (mounted at the app root),
          // pre-filled from current usage. Replaces the old mailto: CTA.
          this.$bus.$emit('open-upgrade-request', {
            users: this.tierUsage?.users,
            workcenters: this.tierUsage?.workcenters,
            workers: this.tierUsage?.workers,
          })
        }
      })
    },

    // Human-friendly noun for a limit key, run through translate() by the caller.
    limitLabel(kind) {
      switch (kind) {
        case 'users': return 'Users'
        case 'workcenters': return 'Workcenters'
        case 'workers': return 'Workers'
        case 'documents': return 'Documents'
        case 'storageBytes': return 'Storage'
        case 'activeOrders': return 'Active orders'
        default: return kind
      }
    },
  },
}
